import type { GridCommonProps, OrderInfo } from '@@partner:types:app'
import type { NestedFilter } from '@@partner:graphql/types'

import React from 'react'
import { formatDate, formatPrice } from '@@partner:helpers/util'
import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { useParams } from 'react-router-dom'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import { isEditable } from '@@partner:helpers/order'

import { Link, generatePath } from 'react-router-dom'
import {
  Button,
  Message,
  Pagination,
  Panel,
  Tag
} from 'rsuite'
import OrderStatusLabel from '@components/order/common/OrderStatusLabel'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable'

import {
  ROUTE_ORDERS_EDIT_ORDER,
  ROUTE_ORDERS_VIEW_ORDER
} from '@@partner:config/routes'
import { ORDERS_PER_PAGE_LIMIT } from '@@partner:config/index'

export interface StoriesTableProps extends GridCommonProps<OrderInfo> {}

export interface CustomerOrdersProps {
  customerId: number
}

export default function CustomerOrders ({ customerId }: CustomerOrdersProps) {
  const { page, tableSort, handlePageSelect, handleSortColumn } =
    useGridHelper()

  const ordersFilter: NestedFilter = {
    fields: [ 'customer_id' ],
    op: 'eq',
    value: [ customerId ]
  }

  const { brandSlug } = useParams<any>()
  const { data, loading, error } = useFetchOrders({
    pager: {
      page,
      limit: ORDERS_PER_PAGE_LIMIT
    },
    filter: ordersFilter,
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    }
  })

  const orders = data?.orders?.edges ?? []

  const pageInfo = data?.orders?.pageInfo

  const showPagination = pageInfo?.lastPage !== 1

  // Колонки для ResponsiveTable
  const columns: ResponsiveTableColumn<OrderInfo>[] = [
    {
      dataKey: 'id',
      header: 'ИД',
      sortable: true,
      flexGrow: 1,
      isCardTitle: true,
      render: (order) => `#${order.id}`
    },
    {
      dataKey: 'status',
      header: 'Статус',
      sortable: true,
      flexGrow: 3,
      isCardBadge: true,
      render: (order) => <OrderStatusLabel status={order.status} />
    },
    {
      dataKey: 'deliverySlot',
      header: 'Слот доставки',
      flexGrow: 3,
      render: (order) => {
        const slot = order.deliverySlot
        if (!slot) {
          return <span className="text-gray-400">—</span>
        }
        return <span>{slot.slotDate} {slot.slotTime}</span>
      }
    },
    {
      dataKey: 'created_at',
      header: 'Даты',
      sortable: true,
      flexGrow: 4,
      render: (order) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {!!order.placedAt && (
            <span style={{ marginBottom: '0.5em' }}>
              Размещен:{' '}
              <Tag color="blue">
                {formatDate(new Date(order.placedAt))}
              </Tag>
            </span>
          )}
          {!!order.assemblingFinishedAt && (
            <span style={{ marginBottom: '0.5em' }}>
              Собран:{' '}
              <Tag color="blue">
                {formatDate(new Date(order.assemblingFinishedAt))}
              </Tag>
            </span>
          )}
          {!!order.deliveryFinishedAt && (
            <span style={{ marginBottom: '0.5em' }}>
              Доставлен:{' '}
              <Tag color="blue">
                {formatDate(new Date(order.deliveryFinishedAt))}
              </Tag>
            </span>
          )}
        </div>
      )
    },
    {
      dataKey: 'total',
      header: 'Суммы (И(Д))',
      sortable: true,
      flexGrow: 4,
      render: (order) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>
            Заказано:{' '}
            <Tag>
              {formatPrice(order.total + order.delivery_total)} {'('}
              {formatPrice(order.delivery_total || 0)}
              {')'}
            </Tag>
          </span>
          <span>
            Собрано:{' '}
            <Tag color="green">
              {formatPrice(order.assembledOrderTotal)} {'('}
              {formatPrice(order.delivery_total || 0)}
              {')'}
            </Tag>
          </span>
        </div>
      )
    }
  ]

  const renderActions = (order: OrderInfo) => {
    const route = isEditable(order.status) ? ROUTE_ORDERS_EDIT_ORDER : ROUTE_ORDERS_VIEW_ORDER
    return (
      <Button
        color="green"
        appearance="primary"
        as={Link}
        to={generatePath(route, {
          brandSlug,
          orderId: order.id
        })}
        size="xs"
      >
        Просмотр
      </Button>
    )
  }

  return (
    <div className="CustomerOrders">
      {error && (
        <Message
          showIcon
          type="error"
          header="При получении данных возникла ошибка!"
        >
          {error.message}
        </Message>
      )}

      <Panel shaded>
        <ResponsiveTable<OrderInfo>
          data={orders}
          columns={columns}
          loading={loading}
          rowHeight={100}
          sortColumn={tableSort.fieldName}
          sortType={tableSort.sortType}
          onSortColumn={handleSortColumn}
          renderActions={renderActions}
          emptyMessage="Нет заказов"
        />

        {showPagination && (
          <Pagination
            last
            first
            next
            prev
            ellipsis
            total={pageInfo?.total ?? 0}
            limit={ORDERS_PER_PAGE_LIMIT}
            activePage={page}
            maxButtons={10}
            onChangePage={handlePageSelect}
          />
        )}
      </Panel>
    </div>
  )
}
